import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectionsApi, notesApi } from '../services/api';
import {
  Collection,
  CreateCollectionPayload,
  UpdateCollectionPayload,
} from '../types/collections';

export const COLLECTIONS_QUERY_KEY = ['collections'];

export const useCollections = () => {
  const queryClient = useQueryClient();

  const { data: collections, isLoading, isError, error } = useQuery<Collection[]>({
    queryKey: COLLECTIONS_QUERY_KEY,
    queryFn: collectionsApi.list,
    staleTime: 30_000,
    gcTime: 10 * 60 * 1000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: COLLECTIONS_QUERY_KEY });

  const createMutation = useMutation({
    mutationFn: (payload: CreateCollectionPayload) => collectionsApi.create(payload),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateCollectionPayload }) =>
      collectionsApi.update(id, payload),
    onSuccess: invalidate,
  });

  // Optimistic: the cover repaints immediately and rolls back if the server refuses.
  const recolorMutation = useMutation({
    mutationFn: ({ id, color }: { id: number; color: string }) =>
      collectionsApi.update(id, { color }),
    onMutate: async ({ id, color }) => {
      await queryClient.cancelQueries({ queryKey: COLLECTIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<Collection[]>(COLLECTIONS_QUERY_KEY);
      queryClient.setQueryData<Collection[]>(COLLECTIONS_QUERY_KEY, (list) =>
        list?.map((c) => (c.id === id ? { ...c, color } : c)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(COLLECTIONS_QUERY_KEY, ctx.previous);
    },
    onSettled: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => collectionsApi.delete(id),
    onSuccess: invalidate,
  });

  const setNoteCollectionMutation = useMutation({
    mutationFn: ({ noteId, collectionId }: { noteId: number; collectionId: number | null }) =>
      notesApi.setNoteCollection(noteId, collectionId),
    onSuccess: invalidate,
  });

  const collectionsList = collections ?? [];

  const getCollectionForNote = (noteId: number): Collection | null =>
    collectionsList.find((c) => c.note_ids.includes(noteId)) ?? null;

  return {
    collections: collectionsList,
    isLoading,
    isError,
    error,
    createCollection: createMutation.mutateAsync,
    updateCollection: updateMutation.mutateAsync,
    recolorCollection: recolorMutation.mutateAsync,
    deleteCollection: deleteMutation.mutateAsync,
    setNoteCollection: setNoteCollectionMutation.mutateAsync,
    getCollectionForNote,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSettingNoteCollection: setNoteCollectionMutation.isPending,
    reload: invalidate,
  };
};
